import { useEffect, useMemo, useState } from "react";

type HardwareComparisonInput = {
  hardwareIdentifier?: string;
  hardwareId?: string;
  deploymentId?: string;
};

type CompetitorPricingApiRow = {
  hardwareName?: unknown;
  updatedCompetitorPrice?: unknown;
};

type CompetitorPricingApiResponse = {
  data?: CompetitorPricingApiRow[];
};

type CompetitorPriceMap = Record<string, number>;

const COMPETITOR_PRICING_API = "/api/competitor-pricing";
const CLIENT_CACHE_TTL_MS = 1000 * 60 * 30; // 30 minutes

let cachedCompetitorPriceMap: CompetitorPriceMap | undefined;
let cachedCompetitorPriceMapAt = 0;
let competitorPriceMapPromise: Promise<CompetitorPriceMap> | null = null;

export const normalizeHardwareIdentifier = (value: string) =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ");

const normalizeCountNotation = (value: string) =>
  value
    .replace(/\b(\d+)\s*x\b/g, "x$1")
    .replace(/\bx\s*(\d+)\b/g, "x$1")
    .replace(/\s+/g, " ")
    .trim();

const buildLookupKeys = (value: string) => {
  const normalized = normalizeCountNotation(normalizeHardwareIdentifier(value));
  if (!normalized) return [];

  const keys = new Set<string>([normalized, normalized.replace(/\s+/g, "")]);
  const countPrefixedMatch = normalized.match(/^(x\d+)\s+(.+)$/);
  const countSuffixedMatch = normalized.match(/^(\d+x)\s+(.+)$/);

  if (countPrefixedMatch) {
    keys.add(`${countPrefixedMatch[2]} ${countPrefixedMatch[1]}`);
    keys.add(`${countPrefixedMatch[2]}${countPrefixedMatch[1]}`);
  }

  if (countSuffixedMatch) {
    keys.add(`${countSuffixedMatch[2]} ${countSuffixedMatch[1]}`);
    keys.add(`${countSuffixedMatch[2]}${countSuffixedMatch[1]}`);
  }

  const countMatch = normalized.match(/\bx(\d+)\b/);
  const cardCount = countMatch?.[1];
  if (cardCount) {
    const withoutCount = normalized
      .replace(/\bx\d+\b/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (withoutCount) {
      keys.add(withoutCount);
      keys.add(withoutCount.replace(/\s+/g, ""));
      keys.add(`${withoutCount} x${cardCount}`);
      keys.add(`${withoutCount}x${cardCount}`);
      keys.add(`x${cardCount} ${withoutCount}`);
      keys.add(`${cardCount}x ${withoutCount}`);
      keys.add(`${withoutCount}x${cardCount}`.replace(/\s+/g, ""));
      keys.add(`x${cardCount}${withoutCount}`.replace(/\s+/g, ""));
      keys.add(`${cardCount}x${withoutCount}`.replace(/\s+/g, ""));
    }
  }

  const leadingNumericMatch = normalized.match(/^(\d+)\b/);
  if (leadingNumericMatch?.[1]) {
    keys.add(leadingNumericMatch[1]);
  }

  return Array.from(keys);
};

const parsePrice = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return undefined;
};

const buildCompetitorPriceMap = (rows: CompetitorPricingApiRow[]) => {
  const priceMap: CompetitorPriceMap = {};

  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const hardwareName =
      typeof row.hardwareName === "string" && row.hardwareName.trim()
        ? row.hardwareName
        : undefined;
    const updatedCompetitorPrice = parsePrice(row.updatedCompetitorPrice);

    if (!hardwareName || !updatedCompetitorPrice) continue;

    for (const key of buildLookupKeys(hardwareName)) {
      if (!priceMap[key]) {
        priceMap[key] = updatedCompetitorPrice;
      }
    }
  }

  return priceMap;
};

const fetchCompetitorPriceMap = async (): Promise<CompetitorPriceMap> => {
  const now = Date.now();
  if (cachedCompetitorPriceMap && now - cachedCompetitorPriceMapAt < CLIENT_CACHE_TTL_MS) {
    return cachedCompetitorPriceMap;
  }

  if (competitorPriceMapPromise) {
    return competitorPriceMapPromise;
  }

  competitorPriceMapPromise = (async () => {
    const response = await fetch(COMPETITOR_PRICING_API, { method: "GET" });
    if (!response.ok) {
      throw new Error(`Failed to fetch competitor pricing: ${response.status}`);
    }

    const payload = (await response.json()) as CompetitorPricingApiResponse;
    const rows = Array.isArray(payload.data) ? payload.data : [];
    const nextMap = buildCompetitorPriceMap(rows);
    cachedCompetitorPriceMap = nextMap;
    cachedCompetitorPriceMapAt = Date.now();
    return nextMap;
  })();

  try {
    return await competitorPriceMapPromise;
  } catch (error) {
    if (cachedCompetitorPriceMap) {
      return cachedCompetitorPriceMap;
    }
    throw error;
  } finally {
    competitorPriceMapPromise = null;
  }
};

/** Starts a background fetch to warm the in-memory cache; errors are ignored. */
export const prefetchCompetitorPriceMap = () => {
  void fetchCompetitorPriceMap().catch(() => {
    // Warm-cache only; callers that need data will fetch again.
  });
};

export const useCompetitorPriceMap = () => {
  const [priceMap, setPriceMap] = useState<CompetitorPriceMap | undefined>(
    cachedCompetitorPriceMap
  );

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const data = await fetchCompetitorPriceMap();
        if (isMounted) {
          setPriceMap(data);
        }
      } catch {
        if (isMounted) {
          setPriceMap(undefined);
        }
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, []);

  return useMemo(() => priceMap ?? cachedCompetitorPriceMap, [priceMap]);
};

const getExactComparisonPrice = (
  { hardwareIdentifier, hardwareId, deploymentId }: HardwareComparisonInput,
  competitorPriceMap?: CompetitorPriceMap
) => {
  const sourcePriceMap = competitorPriceMap ?? cachedCompetitorPriceMap;
  if (!sourcePriceMap) {
    return undefined;
  }

  const candidates = [hardwareIdentifier, deploymentId, hardwareId].filter(
    (value): value is string => Boolean(value)
  );

  for (const candidate of candidates) {
    const keys = buildLookupKeys(candidate);
    for (const key of keys) {
      const price = sourcePriceMap[key];
      if (typeof price === "number" && Number.isFinite(price) && price > 0) {
        return price;
      }
    }
  }

  return undefined;
};

export const getComparedPrice = (
  input: HardwareComparisonInput,
  competitorPriceMap?: CompetitorPriceMap
) => {
  return getExactComparisonPrice(input, competitorPriceMap);
};

export const getOriginalAndDiscountedPriceFromComparison = (
  input: HardwareComparisonInput,
  currentPrice: number,
  fallbackOriginalPrice: number,
  competitorPriceMap?: CompetitorPriceMap
) => {
  const comparedPrice = getComparedPrice(input, competitorPriceMap);
  const originalPrice =
    typeof comparedPrice === "number" && Number.isFinite(comparedPrice) && comparedPrice > 0
      ? comparedPrice
      : fallbackOriginalPrice;
  const discount =
    originalPrice && originalPrice > currentPrice
      ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100)
      : 0;

  return { originalPrice, discount };
};

export const getTotalCostSavingsFromComparison = (
  input: HardwareComparisonInput,
  currentPricePerHour: number,
  totalCost: number,
  competitorPriceMap?: CompetitorPriceMap
) => {
  const comparedPrice = getComparedPrice(input, competitorPriceMap);
  const savingsPercent =
    typeof comparedPrice === "number" &&
    Number.isFinite(comparedPrice) &&
    comparedPrice > 0 &&
    Number.isFinite(currentPricePerHour) &&
    currentPricePerHour > 0 &&
    comparedPrice > currentPricePerHour
      ? Math.round(((comparedPrice - currentPricePerHour) / comparedPrice) * 100)
      : 0;
  const totalSavingsAmount =
    savingsPercent > 0 &&
    Number.isFinite(totalCost) &&
    totalCost > 0 &&
    Number.isFinite(currentPricePerHour) &&
    currentPricePerHour > 0 &&
    typeof comparedPrice === "number" &&
    comparedPrice > currentPricePerHour
      ? (totalCost * (comparedPrice - currentPricePerHour)) / currentPricePerHour
      : 0;

  return { savingsPercent, totalSavingsAmount };
};
